import { FilterInput, SortInput, SupportedSortBy } from '@apeiron/common'
import { useMediaQuery, useTheme } from '@mui/material'
import {
  useFakeQueryParams,
  useQueryParams
} from '@src/hooks/common/router/useQueryParam'
import { ParsedUrlQuery } from 'querystring'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'

const useFilterQuery = <T extends FilterInput, T2 extends SupportedSortBy>(
  props: FilterQueryProps<T, T2>
): FilterQueryHook<T, T2> => {
  const {
    supportUrl = true,
    queryToFilterInput,
    queryToSortInput,
    excludedSearchParams = ['page']
  } = props || {}

  const theme = useTheme()

  const isDesktop = useMediaQuery(theme.breakpoints.up('res1024'))

  const queryHook = useQueryParams(true, isDesktop)

  const fakeQueryHook = useFakeQueryParams(true)

  const [query, setQuery, isReady] = supportUrl ? queryHook : fakeQueryHook

  const [versionedQuery, setVersionedQuery] = useState(query)

  useEffect(() => {
    const listeningQuery = R.omit(excludedSearchParams, query)

    if (!R.equals(listeningQuery, versionedQuery)) {
      setVersionedQuery(listeningQuery)
    }
  }, [excludedSearchParams, query, versionedQuery])

  const filterInput = useMemo(() => {
    return queryToFilterInput(versionedQuery)
  }, [versionedQuery])

  const sortInput = useMemo<SortInput<T2>>(() => {
    return queryToSortInput(versionedQuery)
  }, [versionedQuery])

  const setFilterInput = useCallback(
    (filter: T) => {
      setQuery(filter, sortInput, { page: 1 })
    },
    [setQuery, sortInput]
  )

  const setSortInput = useCallback(
    (sort: SortInput<T2>) => {
      setQuery(filterInput, sort, { page: 1 })
    },
    [setQuery, filterInput]
  )

  const setBothInput = useCallback(
    (filter: T, sort: SortInput<T2>) => {
      setQuery(filter, sort, { page: 1 })
    },
    [setQuery]
  )

  return {
    filterInput,
    isReady,
    sortInput,
    setBothInput,
    setFilterInput,
    setSortInput
  }
}

export type FilterQueryProps<
  T extends FilterInput,
  T2 extends SupportedSortBy
> = {
  supportUrl: boolean
  queryToFilterInput: (query: ParsedUrlQuery) => T
  queryToSortInput: (query: ParsedUrlQuery) => SortInput<T2>
  excludedSearchParams?: Array<string>
}

export type FilterQueryHook<
  T extends FilterInput,
  T2 extends SupportedSortBy
> = {
  filterInput: T
  isReady: boolean
  sortInput: SortInput<T2>
  setBothInput: (filter: T, sort: SortInput<T2>) => void
  setFilterInput: (filter: T) => void
  setSortInput: (sort: SortInput<T2>) => void
}

export default useFilterQuery
